import React, { useState, useEffect, useMemo } from 'react';
import { useToast, useLists } from '../../context';
import { useLang } from '../../i18n';
import { useListNames } from '../../hooks/useListNames';
import { useSelection } from '../../hooks/useSelection';
import { useSaveFailed } from '../../hooks/useSaveFailed';
import { keepInView } from '../shared/dom';
import { moveNamesTo, hideNamesIn } from '../../data/itemActions';
import { normalizeName } from '../../logic/names';

/**
 * The list editor's "Items" section: one list's item names (default: the list being
 * edited) — tick some, then
 *   Move N    → another list: they're suggested there from then on, and any on the open
 *               list move too (viewing another list, the destination defaults to the list
 *               being edited — "bring these in");
 *   Remove N  → out of this list: no longer suggested here. History and the open list
 *               don't change, and noting a name again brings it back.
 * Move acts at once, Remove asks first; both toast with an undo.
 */
export function ListItems({ list, catalogs, boardItems }) {
  const { t, ln } = useLang();
  const { short } = useListNames();
  const notify = useToast();
  const failed = useSaveFailed();
  const lists = useLists();
  const [from, setFrom] = useState(list.id);
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const { selected: sel, toggle, toggleAll, clear, keepOnly } = useSelection();
  useEffect(() => { clear(); setQ(''); setTo(''); }, [from, clear]);

  const names = useMemo(() => (catalogs[from] || []).map(s => s.name), [catalogs, from]);
  // Picks drop out when a name leaves this list (moved / removed here or on another device).
  useEffect(() => { keepOnly(names); }, [names, keepOnly]);
  const nq = normalizeName(q);
  const shown = useMemo(() => (nq ? names.filter(n => normalizeName(`${n} ${ln(n)}`).includes(nq)) : names), [names, nq, ln]);
  const targets = lists.active.filter(l => l.id !== from);
  const dest = to || (from !== list.id ? list.id : (targets[0] && targets[0].id));
  const allOn = shown.length > 0 && shown.every(n => sel.has(n));
  const picked = () => names.filter(n => sel.has(n));

  const doMove = () => {
    const chosen = picked();
    if (!chosen.length || !dest) return;
    const undo = moveNamesTo({ names: chosen, fromId: from, toId: dest, boardItems, lists, onError: failed });
    clear();
    notify(t('lists.moved', { n: chosen.length, list: short(dest) }), undo || undefined);
  };
  const doRemove = () => {
    const chosen = picked();
    if (!chosen.length || !window.confirm(t('lists.removeConfirm', { n: chosen.length, list: short(from) }))) return;
    const undo = hideNamesIn({ names: chosen, listId: from, lists, onError: failed });
    clear();
    notify(t('lists.removed', { n: chosen.length, list: short(from) }), undo || undefined);
  };

  return (
    <div className="do-le__field do-le__mover">
      <div className="do-le__row">
        <span className="do-le__lbl">{t('lists.items')}</span>
        <select className="do-le__select" value={from} onChange={e => setFrom(e.target.value)} aria-label={t('lists.showFrom')}>
          {lists.active.map(l => <option key={l.id} value={l.id}>{l.icon} {short(l)}</option>)}
        </select>
      </div>
      <p className="do-list__sub">{t('lists.itemsHint')}</p>
      {names.length > 6 && (
        <input className="do-le__input do-le__search" type="search" value={q} placeholder={t('lists.searchItems')}
          onFocus={keepInView} onChange={e => setQ(e.target.value)} aria-label={t('lists.searchItems')} />
      )}
      {names.length === 0 ? (
        <p className="do-le__empty">{t('lists.noItems')}</p>
      ) : (
        <>
          <label className="do-le__item do-le__item--all">
            <input type="checkbox" checked={allOn} onChange={() => toggleAll(shown)} />
            <span>{t('lists.selectAll', { n: shown.length })}</span>
          </label>
          <div className="do-le__items">
            {shown.map(n => (
              <label key={n} className={`do-le__item${sel.has(n) ? ' do-le__item--on' : ''}`}>
                <input type="checkbox" checked={sel.has(n)} onChange={() => toggle(n)} />
                <span>{ln(n)}</span>
              </label>
            ))}
          </div>
        </>
      )}
      <div className="do-le__movebar">
        <span className="do-le__movecount">{t('lists.selectedN', { n: sel.size })}</span>
        <span className="do-le__arrow">→</span>
        <select className="do-le__select" value={dest || ''} onChange={e => setTo(e.target.value)} aria-label={t('lists.moveTo')}>
          {targets.map(l => <option key={l.id} value={l.id}>{l.icon} {short(l)}</option>)}
        </select>
        <button type="button" className="do-le__go" onClick={doMove} disabled={!sel.size || !dest}>
          {t('lists.moveSelected', { n: sel.size })}
        </button>
        <button type="button" className="do-le__remove" onClick={doRemove} disabled={!sel.size}>
          🗑 {t('lists.removeSelected', { n: sel.size })}
        </button>
      </div>
    </div>
  );
}
